import React from "react";
import { useAggregatedFiltersContext } from "../AggregatedFilters/AggregatedFilters.context";
import { Button } from "@design-system/Button/Button";
import { OverflowList } from "@design-system/OverflowList/OverflowList";
import { Separator } from "@design-system/Separator/Separator";
import { ClearFiltersButton } from "./ClearFiltersButton";
import styles from "./ActiveFilters.module.scss";

interface ActiveFiltersProps<TFilter extends string = string> {
    activeFilters: TFilter[];
    clearFilters(): void;
    renderVisibleFilters(visibleFilters: TFilter[]): React.ReactNode;
    mergeMultipleFiltersIntoOneButton?: boolean;
    maxVisibleFilters?: number;
    hideClearButton?: boolean;
    disabled?: boolean;
    filtersContainerRef?: React.RefObject<HTMLDivElement | null>;
    /** Maps an active filter id to its overlay tab. Return undefined to skip. */
    resolveFilterTab?: (filter: TFilter) => string | undefined;
}

export const ActiveFilters = <TFilter extends string>({
    activeFilters,
    filtersContainerRef,
    clearFilters,
    renderVisibleFilters,
    mergeMultipleFiltersIntoOneButton = false,
    maxVisibleFilters,
    hideClearButton = false,
    disabled = false,
    resolveFilterTab,
}: ActiveFiltersProps<TFilter>) => {
    const { open: openFilters } = useAggregatedFiltersContext();

    const hasActiveFilters = activeFilters.length > 0;

    const mergeFiltersIntoOneButton =
        mergeMultipleFiltersIntoOneButton && activeFilters.length > 1;

    const openOverflowFilters = (candidates: TFilter[]) => {
        for (const filter of candidates) {
            if (resolveFilterTab) {
                const tab = resolveFilterTab(filter);
                if (tab) {
                    openFilters(tab);
                    return;
                }
                continue;
            }

            openFilters(filter);
            return;
        }

        openFilters();
    };

    if (!hasActiveFilters) {
        return null;
    }

    return (
        <section className={styles.activeFilters}>
            {mergeFiltersIntoOneButton && (
                <>
                    <Separator orientation="vertical" block />
                    <div className={styles.list}>
                        <Button
                            variant="secondary"
                            size="md"
                            onClick={() => openOverflowFilters(activeFilters)}
                        >
                            {activeFilters.length} Filters
                        </Button>
                    </div>
                </>
            )}
            {!mergeFiltersIntoOneButton && (
                <div className={styles.list}>
                    <OverflowList
                        items={activeFilters}
                        renderItems={renderVisibleFilters}
                        onOverflowClick={openOverflowFilters}
                        maxVisible={maxVisibleFilters}
                        containerRef={filtersContainerRef}
                        hideOverflowButton={disabled}
                    />
                </div>
            )}
            {!hideClearButton && <ClearFiltersButton onClick={clearFilters} />}
        </section>
    );
};
